// Post-purchase handoff to /subscribe-thank-you/. Ported from react-app's
// validateAndSubmitSubs success branch (utils/recurly.functions.jsx): the
// thank-you page's conversion tracking (header/footer-thank-you-subscribe.php)
// reads these sessionStorage keys and the `rsid`/`pln`/`promo`/`gift` query
// params, and only counts a "new subscription" when `email` is present.

// Written only by a real, successful purchase (completeOrder below), so finding
// it again means this tab already holds a paid order. See returnToPaidOrder.
const PAID_ORDER_KEY = 'subscription_id';

function put(key, value) {
  try { window.sessionStorage.setItem(key, value == null ? '' : String(value)); } catch { /* storage unavailable */ }
}

// The thank-you page's tags read the whole query (utm_*, gclid, fbclid, pln, dt,
// ...), and the old checkout handed it all over, so start from the URL the order
// was placed from. The order's own answers then win: what was actually bought,
// not what the address said. The plan and date go by the names the checkout
// itself uses (pln, dt); the old checkout's plan and weddingDate are not sent.
export function thankYouQuery(search, order) {
  const q = new URLSearchParams(search);
  if (order.planCode) q.set('pln', order.planCode);
  if (order.wedDate) q.set('dt', order.wedDate);
  if (order.appliedCoupon) q.set('promo', order.appliedCoupon);
  if (order.bonusCode) q.set('bonus', order.bonusCode);
  if (order.audience === 'gift') q.set('gift', 'yes'); else q.delete('gift');
  if (order.subscription_id) q.set('rsid', order.subscription_id);
  return q.toString();
}

export default function completeOrder(order) {
  const email = (order.email || '').trim().replaceAll(' ', '');
  const gift = order.audience === 'gift';

  put('price', order.price);
  put('email', email);
  put('first-name', order.first);
  put('last-name', order.last);
  put('street', order.street);
  put('apt-unit', order.apt);
  put('city', order.city);
  put('country', order.country || 'US');
  put('state', order.region);
  put('zip-postal', order.postal);
  put('phone', order.phone);
  put('gift', gift ? 'yes' : '');
  put('plan', order.planCode);
  put('recurly_invoice_id', order.invoice_id);
  put(PAID_ORDER_KEY, order.subscription_id);

  window.location.href = `/subscribe-thank-you/?${thankYouQuery(window.location.search, order)}`;
  return { ok: true };
}

// Back from the thank-you page is a full page load of ?step=pay, and a payment
// form whose Recurly token is already spent. Saved delivery details were cleared
// by the purchase, so without this the customer gets "We need your delivery
// details again" right after paying, and may try to order twice. The marker above
// says this tab already paid, so send them straight back to that thank-you page.
// Re-pushing (location.assign, not replace) recreates the same [..., pay, thank-you]
// history every time, so the redirect repeats on every Back press.
// Run before React mounts, so the spent form is never drawn. `win` is injectable for tests.
export function returnToPaidOrder(win = window) {
  let id = '';
  try { id = win.sessionStorage.getItem(PAID_ORDER_KEY) || ''; } catch { /* storage unavailable */ }
  const { search } = win.location;
  if (!id || new URLSearchParams(search).get('step') !== 'pay') return false;
  const q = new URLSearchParams(search);
  q.set('rsid', id);
  win.location.assign(`/subscribe-thank-you/?${q.toString()}`);
  return true;
}

// Dropped when Delivery is shown: that is the first screen of a new order, so a
// second order started in the same tab is not taken for a Back press off the
// first one's thank-you page. Back off thank-you never reaches Delivery (the
// redirect above repeats), so this cannot clear it for a Back press.
export function forgetPaidOrder(win = window) {
  try { win.sessionStorage.removeItem(PAID_ORDER_KEY); } catch { /* storage unavailable */ }
}
